// PostHog event names + prop shapes for the Alert Center (RET-1875, plan
// 2026-08-19-001 D7). Today the center ships ZERO events; these land with the
// foundations PR so every surface built on top (units 2-4) instruments from
// its first render. Naming follows the repo convention
// `front.<domain>.<entity>.<action>`; the per-domain constants pattern
// mirrors leadAgentPaywallAnalytics.constants.
//
// Emission rules (owned by the consuming surfaces):
//   - Events fire only while `leads_alert-center` is ON for the workspace —
//     with the flag off there are no alert surfaces, so an event would be
//     a phantom.
//   - Events that stamp point-in-time state (counts) gate on a data-loaded
//     signal, never on render visibility alone (see docs/solutions/
//     logic-errors/once-per-view-analytics-guard-locks-in-preload-zeros.md).
//   - Emit through `useAnalytics().trackEvent` (workspace_id + user_role are
//     auto-injected; events without workspace context are dropped), NEVER the
//     PostHog SDK directly.
//   - Legacy banner event continuity with the center ON (audited in
//     RET-1879; rollout gate on RET-1880): the `addon_*` family keeps firing
//     but its funnel-surface slice migrates — TopDescription's emissions stop,
//     so `addon_*` with source 'funnel' (and the source-less shown) falls to
//     zero; what survives is the Messenger anchor (source 'chat') plus the
//     center's own `front.alerts.*` events. Monitor the split by `source`,
//     not event presence.
//   - `la_paywall_banner_viewed` / `la_seller_bot_paused_viewed` (RET-1891,
//     product decision 2026-08-21): the gated legacy banners were their only
//     emitters, so RET-1879 left them at zero with the flag ON. They are now
//     re-emitted BY THE CENTER, under real-exposure semantics — "viewed"
//     means the alert was on screen, never that the workspace was eligible:
//       · panel-only alerts (`la-acceptance`, `la-trial-activation`, both
//         `slot`) emit when the user OPENS the bell and the alert is present
//         in the rendered list, ONCE per page load per alert (in-memory
//         dedupe — reopening the panel in the same load does not re-emit).
//         An alert that enters the list while the panel is already open
//         counts too: it is exposed.
//       · alerts with a surface visible without user action (`la-seller-paused`,
//         `contextual`, anchored in the sidebar avatar and the funnel header)
//         emit when that anchor mounts, as the legacy strip did.
//     Same event names, same transport, same props as the legacy emitters,
//     plus `source: 'alert-center'`. `alert_id` is carried on the paywall
//     event because the legacy props could NOT tell acceptance from trial
//     activation (both banners built the identical payload) and the center
//     needs the split. Volume DROPS against the legacy baseline by design —
//     the banners emitted per Pipe/Dashboard page load, the panel emits per
//     deliberate open. Do not read that drop as a regression.

import type { tWapiDisconnectionCtaKind } from 'src/alerts/WapiDisconnection.copy';
import type {
  tAlertCtaKind,
  tAlertPlacement,
  tAlertPriority,
} from 'src/alerts/AlertCenter.types';
import type { tLeadAgentBannerViewedProps } from './leadAgentPaywallAnalytics.constants';

export const ALERT_CENTER_EVENTS = {
  /** The wide P0 banner became visible (one per alert per appearance). */
  BANNER_SHOWN: 'front.alerts.banner.shown',
  /** CTA click on the wide P0 banner. */
  BANNER_CTA_CLICKED: 'front.alerts.banner.cta_clicked',
  /**
   * A waiting critical replaced the wide incumbent. This is the swap-churn
   * metric: how often the single wide slot changes hands decides whether the
   * no-dwell promotion (win the arbitration = show NOW) needs sophistication.
   */
  CRITICAL_PROMOTED: 'front.alerts.critical.promoted',
  /** Bell popover opened. Also the crossing signal for a dedicated section. */
  BELL_OPENED: 'front.alerts.bell.opened',
  /**
   * CTA click reported by any Alert Center surface EXCEPT the wide strip: a
   * bell row, an Avisos tab row, the in-page surface, the detail modal's
   * footer. The wide strip has its own `BANNER_CTA_CLICKED` — one click must
   * never land in two funnels. The split is by the SURFACE that reports (each
   * caller declares its `source`; the strip never routes its CTA through this
   * emission), not by the alert's declared placement: a `wide` alert clicked
   * from a bell row still lands here, correctly (RET-1957 U4, review note).
   */
  ROW_CTA_CLICKED: 'front.alerts.row.cta_clicked',
  /**
   * U9 — an alert became visible on a surface the user did NOT have to open.
   * The bell's rows are not this: opening the panel is a deliberate act and
   * the paywall family already reports it under its legacy name. This is the
   * denominator for the in-page surfaces, where the alert is simply there.
   *
   * Once per alert key per page load, on the same module-level ledger the
   * other exposure emitters share, so a remount on the way back to a page does
   * not inflate it.
   */
  ALERT_SHOWN: 'front.alerts.alert.shown',
  /** A dismissible alert (P1/P3) acknowledged from any surface. */
  ALERT_ACKNOWLEDGED: 'front.alerts.alert.acknowledged',
  /** Detail modal (U5) opened. */
  DETAIL_OPENED: 'front.alerts.detail.opened',
  /**
   * AGI-428: the user ticked "No volver a mostrar" on the Meta-pricing
   * notice. It is NOT an acknowledgment and must never be read as one: the
   * bell row stays alive until the backend row expires, and all this turns off
   * is the automatic opening. Paired with `DETAIL_OPENED` + `source: 'auto'`
   * it answers the only question the rollout has about the modal: how many
   * people switch it off rather than close it.
   */
  DETAIL_DISMISSED_FOREVER: 'front.alerts.detail.dismissed_forever',
  /**
   * AGI-428, message 2 (25-sep): the user pressed play on the best-practices
   * video inside the Meta-pricing detail (or opened it in a new tab when the
   * host cannot be embedded). Watching the video IS the message, so paired
   * with `DETAIL_OPENED` on the same `alert_id` it is the rate that says
   * whether message 2 worked, before any CTA click.
   */
  DETAIL_VIDEO_PLAYED: 'front.alerts.detail.video_played',
  /**
   * AGI-886 (U2): the Centro de avisos page (`/alert-center`) was opened. Once
   * per visit, when its question read resolves (also when it fails), never
   * again for the poll or a re-render. Its consumer is the reach in U10: the
   * accounts that open the page each week, by `view`, which is the "before"
   * the feed is read against. The page's only screen event: what happens to
   * the questions themselves is measured from the backend (D10).
   */
  PAGE_VIEWED: 'front.alerts.page.viewed',
} as const;

/**
 * Where a detail modal was opened from.
 *
 * AGI-428 adds `'auto'`, and it is the value the whole rollout is measured on:
 * every other origin is a click, so a funnel that cannot tell "the app opened
 * this" from "the user asked for it" would report the notice as read by
 * people who only had it appear.
 */
export type tAlertDetailOrigin = 'wide' | 'bell' | 'auto';

/**
 * RET-1957 (U4) — which Alert Center surface hosted a clicked CTA. The wide
 * strip is absent on purpose: it emits `BANNER_CTA_CLICKED` for the same
 * click, and one action in two funnels corrupts both.
 *
 * Note this `source` answers a DIFFERENT question from the one on the
 * re-emitted legacy events below (`ALERT_CENTER_SOURCE`, "the center rather
 * than the retired banner"). Inside `front.alerts.*` the emitter is the center
 * by construction, so the useful split is which of its surfaces.
 *
 * AGI-888 (U3): `'alert-feed'` is the feed version of `/alert-center` (its
 * levels 1 and 3). `'feed'` stays the Avisos tab of the tabs view, so the two
 * views can be reconciled before the tabs are retired (U10).
 */
export type tAlertCtaSurface = 'bell' | 'feed' | 'in-page' | 'detail' | 'alert-feed';

// `workspace_id` and `user_role` are injected by useAnalytics — the prop
// shapes below carry only the per-event payload.

/**
 * INT-1653 — what the WhatsApp disconnection notices add to both banner
 * events, so reconnection, Cloud API sessions and support requests can be
 * measured by Meta's cause. Absent on every other alert.
 */
export type tWapiDisconnectionEventProps = {
  /** Meta's cause, verbatim (`PRIMARY_INACTIVITY`…), or null when none came. */
  meta_reason?: string | null;
  /** `USER`, `SYSTEM` or null. */
  meta_initiated_by?: string | null;
  /** The notice's case: `0`…`10`, `8a`, `8b`, `8c_same`, `8c_other`. */
  disconnection_case?: string;
  viewer_role?: string;
};

export type tAlertBannerShownProps = {
  alert_id: string;
  /** The winner's rank in the window arbitration table (0 = shortest window). */
  window_rank: number;
  /** How many criticals were left waiting in the bell at that moment. */
  waiting_count: number;
} & tWapiDisconnectionEventProps;

export type tAlertBannerCtaClickedProps = {
  alert_id: string;
  /**
   * The dispatch kind, or, on the WhatsApp disconnection notices, what the
   * button is for: `reconnect`, `schedule_session`, `cloud_api_article`,
   * `video`, `support`, `create_template` or `dismiss`.
   */
  cta_kind: tAlertCtaKind | tWapiDisconnectionCtaKind;
} & tWapiDisconnectionEventProps;

export type tAlertCriticalPromotedProps = {
  /** Alert that left the wide slot (null when the slot was empty). */
  from_alert_id: string | null;
  to_alert_id: string;
};

export type tAlertBellOpenedProps = {
  /** Badge semantics (D12): clearable P1 pending + P3 not dismissed. */
  pending_count: number;
  /** Criticals waiting behind the wide winner — NOT part of the badge. */
  waiting_criticals_count: number;
};

/**
 * RET-1957 (U4) — emitted by the SHARED dispatch (`useAlertCta`), which is the
 * only place every surface's CTA passes through; a per-surface emitter would
 * have drifted into "the click counts in the bell but not in the tab".
 *
 * That shared vantage point is also why the bell SECTION this shipped with is
 * gone: the dispatch cannot know it, and it was redundant anyway — the panel
 * partitions by tier, so `priority` reconstructs it (p0 waiting, p1 decisions,
 * p3 news) and `placement` says which surface delivered the alert, which
 * `section` never could.
 */
export type tAlertRowCtaClickedProps = {
  alert_id: string;
  /** The alert's tier. Reconstructs the bell section, and splits the funnel. */
  priority: tAlertPriority;
  /** Which dispatch branch ran. `detail` is the RET-1956 payment path. */
  cta_kind: tAlertCtaKind;
  /**
   * Where the ENGINE delivered the alert (RET-1957: in-page vs slot). Not the
   * same as `source`: a `slot` alert clicked from the detail footer reports
   * `placement: 'slot'`, `source: 'detail'`.
   */
  placement: tAlertPlacement;
  /** Which surface hosted the button the user actually clicked. */
  source: tAlertCtaSurface;
};

/**
 * U9 — `front.alerts.alert.shown`. Pairs with `ROW_CTA_CLICKED` and
 * `ALERT_ACKNOWLEDGED` on `alert_id` + `source`, which is what turns the
 * explainer into a funnel: seen → acted / dismissed. `user_role` is injected
 * by `useAnalytics`, so the role-branched variants split without a prop here.
 */
export type tAlertShownProps = {
  alert_id: string;
  priority: tAlertPriority;
  /** Which surface the user saw it on. */
  source: tAlertCtaSurface;
};

export type tAlertAcknowledgedProps = {
  alert_id: string;
  /** Occurrence segment of the acknowledged key (billing period / fallback). */
  period_key: string | null;
  /**
   * U9 — which surface the dismissal came from. The same declaration is
   * dismissible from the bell, the Avisos tab and (since U9) the in-page
   * banner; without this the three collapse into one number.
   */
  source: tAlertCtaSurface;
};

export type tAlertDetailOpenedProps = {
  alert_id: string;
  from: tAlertDetailOrigin;
  /**
   * The same value as `from`, under the name the plan's monitoring queries
   * use (`source: auto`). Carried as well as `from` rather than instead of it
   * so the existing prop keeps its meaning for the surfaces that already
   * declare it.
   */
  source: tAlertDetailOrigin;
};

/**
 * AGI-428: the notice's own dimensions, stamped on `detail.opened`,
 * `row.cta_clicked` and `dismissed_forever` alike.
 *
 * They are what makes the three events a funnel worth reading: the notice
 * ships in 26 batches across five copies and three urgencies, so an open rate
 * without `urgency` and `segment` averages over populations that were sent
 * deliberately different things. `cta_url` is here because one of the three
 * CTAs ships EMPTY (the webinar link is pending): without it, a zero
 * click-through on urgency media reads as a copy failure rather than as a
 * button that was never rendered.
 */
export type tMetaPricingEventProps = {
  segment: string;
  urgency: string;
  key_account: boolean;
  cta_url: string;
};

export type tAlertDetailDismissedForeverProps = tMetaPricingEventProps & {
  alert_id: string;
};

/**
 * AGI-886 (U2): `front.alerts.page.viewed`. `view` is the version of the page
 * that was open: `tabs` today, `feed` once U3 emits it, so U10 counts the reach
 * of each. `open_questions` is the number the page showed for its questions,
 * or null when the read failed or was refused and it showed none.
 */
export type tAlertsPageViewedProps = {
  view: 'tabs' | 'feed';
  open_questions: number | null;
};

/**
 * AGI-428, message 2. `player` says where it played: `inline` inside the
 * modal (YouTube, Drive) or `new_tab` for a host that cannot be embedded, so
 * a drop in inline plays after a link change reads as the host, not the copy.
 */
export type tAlertDetailVideoPlayedProps = tMetaPricingEventProps & {
  alert_id: string;
  player: 'inline' | 'new_tab';
};

// ── Legacy paywall events re-emitted by the center (RET-1891) ───────────────
// These keep the LEGACY names (`LEAD_AGENT_PAYWALL_EVENTS.BANNER_VIEWED` /
// `.SELLER_PAUSED_VIEWED`) and the legacy transport, so the historical series
// stays continuous. Only the payload grows, and only by what identifies the
// new emitter.

/** `source` every Alert Center surface stamps on a re-emitted legacy event. */
export const ALERT_CENTER_SOURCE = 'alert-center';

/** The two `slot` paywall alerts whose exposure re-emits `banner_viewed`. */
export const PAYWALL_VIEWED_ALERT_IDS = [
  'la-acceptance',
  'la-trial-activation',
] as const;

export type tPaywallViewedAlertId = (typeof PAYWALL_VIEWED_ALERT_IDS)[number];

/**
 * `la_paywall_banner_viewed` as the center emits it: the legacy props built by
 * `buildBannerViewedProps` verbatim, plus the emitter identity. `alert_id` is
 * NOT decoration — the acceptance banner and the trial-activation banner fed
 * the identical legacy payload, so without it the two panel rows would be
 * indistinguishable in the funnel.
 */
export type tAlertCenterPaywallViewedProps = tLeadAgentBannerViewedProps & {
  alert_id: tPaywallViewedAlertId;
  source: typeof ALERT_CENTER_SOURCE;
};

/**
 * `la_seller_bot_paused_viewed` as the center emits it. `pause_reason` mirrors
 * the legacy strip exactly, including its `'unknown'` fallback for a pause the
 * backend could not attribute.
 */
export type tAlertCenterSellerPausedViewedProps = {
  pause_reason: string;
  source: typeof ALERT_CENTER_SOURCE;
};
